import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
  Badge,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  previewEligible,
  PreviewEligibleRequest,
  PreviewEligibleResponse,
  Project,
} from "utils/apiService/PinkSale";

// Helper function to get week number
const getWeekNumber = (date: Date): number => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

// Format currency
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Format date
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString();
};

// Get chain name
const getChainName = (chainId: number) => {
  const chainMap: { [key: number]: string } = {
    1: "Ethereum",
    56: "BSC",
    501424: "Solana",
  };
  return chainMap[chainId] || `Chain ${chainId}`;
};

const PreviewEligibleProjects = () => {
  const toast = useToast();
  const {
    isOpen: isRawDataOpen,
    onOpen: onRawDataOpen,
    onClose: onRawDataClose,
  } = useDisclosure();

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] =
    useState<PreviewEligibleResponse | null>(null);
  const [previewForm, setPreviewForm] = useState<PreviewEligibleRequest>({
    weekNumber: getWeekNumber(new Date()),
    year: new Date().getFullYear(),
    timestamp: new Date().toISOString(),
    criteria: [
      {
        type: "minRaised",
        enabled: true,
        params: {
          amountUsd: 1000,
        },
      },
    ],
    isPreviewAllProjects: true,
  });
  const [selectedProjectRawData, setSelectedProjectRawData] =
    useState<Project | null>(null);
  const [criteriaJson, setCriteriaJson] = useState<string>("");
  const [criteriaError, setCriteriaError] = useState<string>("");

  // Handle preview with specific form data
  const handlePreviewEligibleWithForm = async (
    formData: PreviewEligibleRequest
  ) => {
    try {
      setPreviewLoading(true);
      const data = await previewEligible(formData);
      setPreviewData(data);
      toast({
        title: "Success",
        description: `Found ${data.totalEligible} eligible projects`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to preview eligible projects",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Auto-load current data when component mounts
  useEffect(() => {
    const now = new Date();
    const defaultCriteria = [
      {
        type: "minRaised",
        enabled: true,
        params: {
          amountUsd: 1000,
        },
      },
    ];
    const updatedForm: PreviewEligibleRequest = {
      weekNumber: getWeekNumber(now),
      year: now.getFullYear(),
      timestamp: now.toISOString(),
      criteria: defaultCriteria,
      isPreviewAllProjects: true,
    };
    setPreviewForm(updatedForm);
    setCriteriaJson(JSON.stringify(defaultCriteria, null, 2));

    // Auto-load the data
    handlePreviewEligibleWithForm(updatedForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle criteria JSON change
  const handleCriteriaJsonChange = (value: string) => {
    setCriteriaJson(value);
    setCriteriaError("");

    if (!value.trim()) {
      setCriteriaError("Criteria cannot be empty");
      return;
    }

    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) {
        setCriteriaError("Criteria must be an array");
        return;
      }
      setPreviewForm({
        ...previewForm,
        criteria: parsed,
      });
    } catch (error: any) {
      setCriteriaError(
        error?.message || "Invalid JSON format. Please check your syntax."
      );
    }
  };

  // Handle preview eligible projects
  const handlePreviewEligible = async () => {
    if (criteriaError) {
      toast({
        title: "Validation Error",
        description: "Please fix the criteria JSON before submitting",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const parsedCriteria = JSON.parse(criteriaJson);
      if (!Array.isArray(parsedCriteria)) {
        toast({
          title: "Validation Error",
          description: "Criteria must be an array",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const formData: PreviewEligibleRequest = {
        ...previewForm,
        criteria: parsedCriteria,
      };
      await handlePreviewEligibleWithForm(formData);
    } catch (error: any) {
      toast({
        title: "Validation Error",
        description: error?.message || "Invalid criteria JSON",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle view raw data
  const handleViewRawData = (project: Project) => {
    setSelectedProjectRawData(project);
    onRawDataOpen();
  };

  return (
    <>
      <VStack spacing={6} align="stretch">
        {/* Form Section */}
        <Box p={6} bg="white" borderRadius="md" boxShadow="sm">
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            Preview Configuration
          </Text>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Week Number</FormLabel>
                <NumberInput
                  value={previewForm.weekNumber}
                  onChange={(valueString, valueNumber) =>
                    setPreviewForm({
                      ...previewForm,
                      weekNumber: valueNumber,
                    })
                  }
                  min={1}
                  max={53}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Year</FormLabel>
                <NumberInput
                  value={previewForm.year}
                  onChange={(valueString, valueNumber) =>
                    setPreviewForm({ ...previewForm, year: valueNumber })
                  }
                  min={2020}
                  max={2100}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>Timestamp</FormLabel>
              <Input
                type="datetime-local"
                value={
                  previewForm.timestamp
                    ? new Date(previewForm.timestamp).toISOString().slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  setPreviewForm({
                    ...previewForm,
                    timestamp: new Date(e.target.value).toISOString(),
                  })
                }
              />
            </FormControl>

            <FormControl isInvalid={!!criteriaError}>
              <FormLabel>Criteria (JSON Array)</FormLabel>
              <Textarea
                value={criteriaJson}
                onChange={(e) => handleCriteriaJsonChange(e.target.value)}
                placeholder='[{"type": "minRaised", "enabled": true, "params": {"amountUsd": 1000}}]'
                fontFamily="monospace"
                fontSize="sm"
                minH="150px"
                resize="vertical"
              />
              {criteriaError && (
                <FormErrorMessage>{criteriaError}</FormErrorMessage>
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                Enter criteria as a JSON array. Example:{" "}
                {`[{"type": "minRaised", "enabled": true, "params": {"amountUsd": 1000}}]`}
              </Text>
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0}>Preview All Projects</FormLabel>
              <Switch
                isChecked={previewForm.isPreviewAllProjects}
                onChange={(e) =>
                  setPreviewForm({
                    ...previewForm,
                    isPreviewAllProjects: e.target.checked,
                  })
                }
              />
            </FormControl>

            <Button
              colorScheme="blue"
              onClick={handlePreviewEligible}
              isLoading={previewLoading}
              loadingText="Loading..."
              size="lg"
            >
              Preview Eligible Projects
            </Button>
          </VStack>
        </Box>

        {/* Results Section */}
        {previewData && (
          <Box p={6} bg="white" borderRadius="md" boxShadow="sm">
            <VStack spacing={4} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontSize="xl" fontWeight="bold">
                  Results
                </Text>
                <Badge colorScheme="green" fontSize="md" p={2}>
                  Total Eligible: {previewData.totalEligible}
                </Badge>
              </Flex>

              {previewData.eligibilityPeriod && (
                <Box p={4} bg="gray.50" borderRadius="md">
                  <Text fontWeight="semibold" mb={2}>
                    Eligibility Period:
                  </Text>
                  <Text fontSize="sm">
                    Start: {formatDate(previewData.eligibilityPeriod.startTime)}
                  </Text>
                  <Text fontSize="sm">
                    End: {formatDate(previewData.eligibilityPeriod.endTime)}
                  </Text>
                </Box>
              )}

              {previewData.projects && previewData.projects.length > 0 ? (
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Token Name</Th>
                        <Th>Symbol</Th>
                        <Th>Chain</Th>
                        <Th>Pool Address</Th>
                        <Th>Total Raised (USD)</Th>
                        <Th>Finish Time</Th>
                        <Th>Status</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {previewData.projects.map((project: Project) => (
                        <Tr key={project.launchpadId}>
                          <Td fontWeight="medium">{project.tokenName}</Td>
                          <Td>
                            <Badge colorScheme="blue">
                              {project.tokenSymbol}
                            </Badge>
                          </Td>
                          <Td>{getChainName(project.chainId)}</Td>
                          <Td>
                            <Text
                              fontSize="xs"
                              fontFamily="mono"
                              cursor="pointer"
                              _hover={{ color: "blue.500" }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  project.poolAddress
                                );
                                toast({
                                  title: "Copied",
                                  description:
                                    "Pool address copied to clipboard",
                                  status: "success",
                                  duration: 2000,
                                  isClosable: true,
                                });
                              }}
                            >
                              {project.poolAddress.slice(0, 10)}...
                            </Text>
                          </Td>
                          <Td>{formatCurrency(project.totalRaisedUSD)}</Td>
                          <Td fontSize="xs">
                            {formatDate(project.finishTime)}
                          </Td>
                          <Td>
                            {Object.values(project.evaluationDetails).every(
                              (detail) => detail.passed
                            ) ? (
                              <Badge colorScheme="green">Eligible</Badge>
                            ) : (
                              <Badge colorScheme="red">Not Eligible</Badge>
                            )}
                          </Td>
                          <Td>
                            <Button
                              size="xs"
                              colorScheme="purple"
                              onClick={() => handleViewRawData(project)}
                            >
                              Review Raw Data
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Center py={10}>
                  <Text>No eligible projects found</Text>
                </Center>
              )}
            </VStack>
          </Box>
        )}
      </VStack>

      {/* Raw Data Modal for Preview Eligible Projects */}
      <Modal
        isOpen={isRawDataOpen}
        onClose={onRawDataClose}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Raw Data - {selectedProjectRawData?.tokenName || ""} (
            {selectedProjectRawData?.tokenSymbol || ""})
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              maxH="70vh"
              overflowY="auto"
            >
              {selectedProjectRawData ? (
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    fontFamily: "monospace",
                    fontSize: "12px",
                  }}
                >
                  {JSON.stringify(selectedProjectRawData, null, 2)}
                </pre>
              ) : (
                <Text>No data selected</Text>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                if (selectedProjectRawData) {
                  navigator.clipboard.writeText(
                    JSON.stringify(selectedProjectRawData, null, 2)
                  );
                  toast({
                    title: "Copied",
                    description: "Raw data copied to clipboard",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                  });
                }
              }}
            >
              Copy to Clipboard
            </Button>
            <Button colorScheme="gray" onClick={onRawDataClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PreviewEligibleProjects;
